import { useMemo } from 'react';
import { Megaphone } from 'lucide-react';
import { Tooltip, KPI_TOOLTIPS } from '@/components/ui';
import { formatCurrency } from '@/utils';
import type { Lead, Deal } from '@/types';

interface LeadSourcesCardProps {
  data: Array<{ source: string; count: number; value: number }>;
  totalLeads: number;
  isLoading?: boolean;
  onSourceClick?: (source: string, leads: Lead[]) => void;
  filteredLeads: Lead[];
  convertedLeads: Array<{ lead: Lead; deal: Deal; daysToConvert: number }>;
}

const sourceConfig: Record<string, { bg: string; text: string; border: string; icon: string; description?: string }> = {
  'Referral': { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30', icon: '🤝', description: 'Customer or contact referred this lead' },
  'Customer Referral': { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30', icon: '🤝', description: 'Existing customer referred this lead' },
  'Referral Partner': { bg: 'bg-teal-500/20', text: 'text-teal-400', border: 'border-teal-500/30', icon: '🔗', description: 'Partner organization referred this lead' },
  'Network': { bg: 'bg-violet-500/20', text: 'text-violet-400', border: 'border-violet-500/30', icon: '🌐', description: 'Personal/professional network connection' },
  'Website': { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30', icon: '🌍', description: 'Inbound from protagona.com (forms, contact page)' },
  'LinkedIn': { bg: 'bg-sky-500/20', text: 'text-sky-400', border: 'border-sky-500/30', icon: '💼', description: 'LinkedIn content engagement, InMail, or direct message' },
  'Cold Outreach': { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30', icon: '📞', description: 'Outbound email sequences (Apollo), cold calls, or unsolicited contact' },
  'Conference': { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30', icon: '🎤', description: 'In-person event: conferences, summits, trade shows' },
  'AWS - Event': { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30', icon: '🎟️', description: 'AWS-hosted event (re:Invent, Summit, Immersion Day)' },
  'Webinar': { bg: 'bg-fuchsia-500/20', text: 'text-fuchsia-400', border: 'border-fuchsia-500/30', icon: '🎥', description: 'Virtual webinar attendance or registration' },
  'Partner': { bg: 'bg-pink-500/20', text: 'text-pink-400', border: 'border-pink-500/30', icon: '🤜', description: 'Strategic partner introduction' },
  'Strategic Partner': { bg: 'bg-pink-500/20', text: 'text-pink-400', border: 'border-pink-500/30', icon: '🤜', description: 'Strategic partner introduction or co-sell' },
  'Inbound': { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30', icon: '📥', description: 'Generic inbound lead (form, email, etc.)' },
  'AWS - Inbound': { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30', icon: '☁️', description: 'Lead came through AWS partner network' },
  'Existing Client': { bg: 'bg-cyan-500/20', text: 'text-cyan-400', border: 'border-cyan-500/30', icon: '⭐', description: 'Expansion opportunity from existing customer' },
  'Apollo': { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30', icon: '🚀', description: 'Imported from Apollo.io visitor tracking' },
  'Apollo - Contact Sync': { bg: 'bg-orange-400/20', text: 'text-orange-300', border: 'border-orange-400/30', icon: '🔄', description: 'Synced contact from Apollo enrichment' },
  'Unknown': { bg: 'bg-zinc-500/20', text: 'text-zinc-400', border: 'border-zinc-500/30', icon: '❓', description: 'No source attributed' },
};

const getSourceDescription = (source: string): string =>
  sourceConfig[source]?.description || source;

const getSourceConfig = (source: string) =>
  sourceConfig[source] || { bg: 'bg-indigo-500/20', text: 'text-indigo-400', border: 'border-indigo-500/30', icon: '📊' };

export function LeadSourcesCard({ data, totalLeads, isLoading, onSourceClick, filteredLeads, convertedLeads }: LeadSourcesCardProps) {
  const dealValueBySource = useMemo(() => {
    const values: Record<string, { dealValue: number; dealCount: number }> = {};
    convertedLeads.forEach(({ lead, deal }) => {
      const source = lead.source || 'Unknown';
      if (!values[source]) {
        values[source] = { dealValue: 0, dealCount: 0 };
      }
      values[source].dealValue += deal.value || 0;
      values[source].dealCount += 1;
    });
    return values;
  }, [convertedLeads]);

  const totalDealValue = convertedLeads.reduce((sum, { deal }) => sum + (deal.value || 0), 0);
  const totalConversions = convertedLeads.length;

  const handleSourceClick = (source: string) => {
    if (onSourceClick) {
      const sourceLeads = filteredLeads.filter(l => (l.source || 'Unknown') === source);
      onSourceClick(source, sourceLeads);
    }
  };

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Megaphone className="h-4 w-4 text-purple-400" />
          <h3 className="text-sm font-semibold text-white">Lead Sources</h3>
        </div>
        <span className="text-xs text-zinc-500">{data.length} sources</span>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-zinc-600 border-t-purple-400" />
        </div>
      ) : data.length === 0 ? (
        <p className="text-xs text-zinc-500 text-center py-4">No source data</p>
      ) : (
        <>
          {/* Summary KPIs */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            <div className="rounded-lg bg-zinc-800/50 p-2 text-center">
              <p className="text-lg font-bold text-white">{totalLeads}</p>
              <p className="text-[10px] text-zinc-500">Total Leads</p>
            </div>
            <div className="rounded-lg bg-zinc-800/50 p-2 text-center">
              <p className="text-lg font-bold text-emerald-400">{totalConversions}</p>
              <p className="text-[10px] text-zinc-500">Converted</p>
            </div>
            <div className="rounded-lg bg-zinc-800/50 p-2 text-center">
              <p className="text-lg font-bold text-[#F78C4E]">{formatCurrency(totalDealValue)}</p>
              <p className="text-[10px] text-zinc-500">Deal Value</p>
            </div>
            <div className="rounded-lg bg-zinc-800/50 p-2 text-center">
              <p className="text-lg font-bold text-cyan-400">
                {totalLeads > 0 ? ((totalConversions / totalLeads) * 100).toFixed(0) : 0}%
              </p>
              <Tooltip {...KPI_TOOLTIPS.conversionRate}>
                <p className="text-[10px] text-zinc-500">Conv. Rate</p>
              </Tooltip>
            </div>
          </div>

          {/* Top Performer Badge */}
          {(() => {
            const topByDealValue = data
              .map(d => ({
                ...d,
                dealData: dealValueBySource[d.source] || { dealValue: 0, dealCount: 0 }
              }))
              .filter(d => d.dealData.dealValue > 0)
              .sort((a, b) => b.dealData.dealValue - a.dealData.dealValue)[0];

            if (!topByDealValue) return null;

            const config = getSourceConfig(topByDealValue.source);
            return (
              <button
                onClick={() => handleSourceClick(topByDealValue.source)}
                className={`rounded-lg ${config.bg} border ${config.border} p-2 mb-3 flex items-center justify-between w-full text-left cursor-pointer hover:brightness-110 transition-all`}
                title={getSourceDescription(topByDealValue.source)}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm">{config.icon}</span>
                  <div>
                    <p className={`text-xs font-medium ${config.text}`}>
                      🏆 Top Converter: {topByDealValue.source}
                    </p>
                    <p className="text-[10px] text-zinc-400">
                      {topByDealValue.dealData.dealCount} deals from {topByDealValue.count} leads
                    </p>
                  </div>
                </div>
                <span className={`text-sm font-bold ${config.text}`}>
                  {formatCurrency(topByDealValue.dealData.dealValue)}
                </span>
              </button>
            );
          })()}

          {/* Source List */}
          <div className="space-y-2">
            {data.map(({ source, count }) => {
              const config = getSourceConfig(source);
              const dealData = dealValueBySource[source] || { dealValue: 0, dealCount: 0 };
              const conversionRate = count > 0 ? (dealData.dealCount / count) * 100 : 0;

              return (
                <button
                  key={source}
                  onClick={() => handleSourceClick(source)}
                  className={`w-full rounded-lg border ${config.border} ${config.bg} p-2.5 hover:opacity-80 transition-all group`}
                  title={getSourceDescription(source)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-base">{config.icon}</span>
                      <div className="text-left">
                        <p className={`text-xs font-medium ${config.text}`}>{source}</p>
                        <p className="text-[10px] text-zinc-500">
                          {count} leads • {conversionRate.toFixed(0)}% converted
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-bold ${dealData.dealCount > 0 ? 'text-emerald-400' : 'text-zinc-500'}`}>
                        {dealData.dealCount > 0 ? formatCurrency(dealData.dealValue) : '—'}
                      </p>
                      <p className="text-[10px] text-zinc-400">
                        {dealData.dealCount} deal{dealData.dealCount !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
